<template>
  <div class="savings-goals-container">
    <!-- Loading State (Requirement 8.6) -->
    <div v-if="isLoading && goals.length === 0" class="loading-container">
      <div class="card">
        <div class="card-body text-center py-5">
          <div class="spinner-border text-primary mb-3" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <h5 class="text-muted">Loading your savings goals...</h5>
          <p class="text-muted mb-0">Please wait while we fetch your data</p>
        </div>
      </div>
    </div>

    <!-- Error State (Requirement 8.6) -->
    <div v-else-if="error" class="error-container">
      <div class="card border-danger">
        <div class="card-body text-center py-5">
          <div class="error-icon mb-3">
            <i class="fa-solid fa-exclamation-triangle text-danger"></i>
          </div>
          <h5 class="text-danger">Error Loading Goals</h5>
          <p class="text-muted mb-3">{{ error }}</p>
          <button class="btn btn-outline-primary" @click="handleRetryLoad">
            <i class="fa-solid fa-refresh me-2"></i>
            Try Again
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else>
      <!-- Active Goals (moved to top for prominence) -->
      <div v-if="activeGoals.length > 0" class="card mb-3">
        <div class="card-header">
          <div class="header-icon">
            <i class="fa-solid fa-bullseye"></i>
          </div>
          <div class="header-content">
            <h3 class="card-title">Active Goals ({{ activeGoals.length }})</h3>
            <p class="card-subtitle">Goals you're actively working toward</p>
          </div>
        </div>
        <div class="card-body">
          <!-- Variable Distribution Section -->
          <div v-if="adjustedRemainingBudget > 0" class="distribute-budget-section nested-container mb-3">
            <div class="distribute-header">
              <h6 class="mb-1">
                <i class="fa-solid fa-coins me-2"></i>
                Contribute to Savings Goals
              </h6>
              <p class="text-muted mb-3">
                You have ${{ formatCurrency(adjustedRemainingBudget) }} available today.
                Use the slider to choose how much to contribute toward your {{ activeGoals.length }} active goal{{
                  activeGoals.length > 1 ? 's' : '' }}.
              </p>
            </div>

            <!-- Amount Slider -->
            <div class="amount-slider-section mb-4">
              <label for="distributionSlider" class="form-label fw-semibold mb-2">
                <i class="fa-solid fa-sliders me-1"></i>
                Contribution Amount: ${{ formatCurrency(distributionAmount) }}
              </label>

              <div class="slider-container">
                <div class="slider-wrapper">
                  <input type="range" class="form-range" id="distributionSlider" :min="0" :max="adjustedRemainingBudget"
                    :step="0.01" v-model.number="distributionAmount" :disabled="isDistributing"
                    @input="handleSliderInput">

                  <!-- Today's Budget Marker -->
                  <div v-if="todayRemainingBudget > 0 && todayRemainingBudget <= adjustedRemainingBudget"
                    class="slider-marker today-marker" :style="{ left: getMarkerPosition(todayRemainingBudget) + '%' }"
                    @click="snapToTodayBudget">
                    <div class="marker-line"></div>
                  </div>
                </div>

                <div class="slider-labels d-flex justify-content-between mt-1">
                  <small class="text-muted">$0</small>
                  <small class="text-muted">${{ formatCurrency(adjustedRemainingBudget) }}</small>
                </div>
              </div>
            </div>

            <!-- Distribution Preview -->
            <div v-if="distributionAmount > 0" class="distribution-preview nested-container mb-3">
              <div class="preview-header d-flex align-items-center mb-2">
                <i class="fa-solid fa-eye me-2"></i>
                <span class="fw-semibold">Contribution Preview</span>
              </div>

              <!-- Legend -->
              <div class="distribution-legend mb-2 pb-3">
                <div class="legend-item">
                  <div class="legend-marker"></div>
                  <span class="legend-text">Today's Target: ${{ formatCurrency(todayRemainingBudget) }}</span>
                </div>
              </div>

              <div class="preview-details">
                <div class="row g-2">
                  <div class="col-6">
                    <small class="text-muted d-block">Total Contribution</small>
                    <span class="fw-semibold">${{ formatCurrency(distributionAmount) }}</span>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">Per Goal</small>
                    <span class="fw-semibold">${{ formatCurrency(distributionAmount / activeGoals.length) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Distribute Button -->
            <div class="distribute-action text-center">
              <button class="btn btn-outline-primary btn-lg dist-btn" @click="handleDistributeCustomAmount"
                :disabled="isDistributing || distributionAmount <= 0">
                <span v-if="isDistributing" class="spinner-border spinner-border-xs me-2" role="status"></span>
                <i v-else class="fa-solid fa-share-nodes me-2"></i>
                <span v-if="isDistributing">Contributing...</span>
                <span v-else-if="distributionAmount <= 0">Select Amount to Contribute</span>
                <span v-else>Contribute ${{ formatCurrency(distributionAmount) }}</span>
              </button>
            </div>
          </div>

          <div class="goals-grid">
            <div v-for="goal in activeGoals" :key="goal.id" class="goal-item nested-container">
              <!-- Goal Header -->
              <div class="goal-header">
                <div class="goal-info">
                  <h5 class="goal-name">{{ goal.name }}</h5>
                  <span class="goal-status">{{ getGoalStatusText(goal) }}</span>
                </div>
                <div class="goal-actions">
                  <!-- Quick Contribution Button -->
                  <button class="btn btn-sm btn-primary me-2" type="button"
                    :title="`Add daily target ($${formatCurrency(this.getGoalDailyAllocation(goal) || 0)})`"
                    @click="handleQuickContribution(goal.id, this.getGoalDailyAllocation(goal) || 0)"
                    :disabled="this.getGoalStatus(goal) !== 'active' || contributingGoals.has(goal.id)">
                    <span v-if="contributingGoals.has(goal.id)" class="spinner-border spinner-border-xs me-1"
                      role="status"></span>
                    <i v-else class="fa-solid fa-plus me-1"></i>
                    <span v-if="contributingGoals.has(goal.id)"> Adding</span>
                    <span v-else>${{ formatCurrency(this.getGoalDailyAllocation(goal) || 0) }}</span>
                  </button>

                  <div class="dropdown">
                    <button class="btn btn-sm btn-outline-secondary dropdown-toggle pe-3 ps-3" type="button"
                      :id="`goalActions${goal.id}`" data-bs-toggle="dropdown" aria-expanded="false">
                      <i class="fa-solid fa-ellipsis-vertical"></i>
                    </button>
                    <ul class="dropdown-menu" :aria-labelledby="`goalActions${goal.id}`">
                      <!-- <li v-if="this.getGoalStatus(goal) === 'active'">
                        <button class="dropdown-item" @click="handlePauseGoal(goal.id)">
                          <i class="fa-solid fa-pause me-2"></i>Pause Goal
                        </button>
                      </li>
                      <li v-if="this.getGoalStatus(goal) === 'paused'">
                        <button class="dropdown-item" @click="handleResumeGoal(goal.id)">
                          <i class="fa-solid fa-play me-2"></i>Resume Goal
                        </button>
                      </li>
                      <li v-if="this.getGoalStatus(goal) !== 'completed'">
                        <button class="dropdown-item" @click="handleMarkCompleted(goal.id)">
                          <i class="fa-solid fa-check me-2"></i>Mark Complete
                        </button>
                      </li>
                      <li><hr class="dropdown-divider"></li> -->
                      <li>
                        <button class="dropdown-item text-danger" @click="handleDeleteGoal(goal.id)">
                          <i class="fa-solid fa-trash me-2"></i>Delete Goal
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Goal Progress -->
              <div class="goal-progress">
                <div class="progress-info">
                  <span class="progress-text">
                    ${{ formatCurrency(this.getGoalProgress(goal.id) || 0) }} of ${{ formatCurrency(goal.targetAmount)
                    }}
                  </span>
                  <span class="progress-percentage">{{ Math.round(this.getGoalProgressPercentage(goal) || 0) }}%</span>
                </div>
                <div class="progress">
                  <div class="progress-bar" :class="getProgressBarClass(goal)" role="progressbar"
                    :style="{ width: Math.min(100, this.getGoalProgressPercentage(goal) || 0) + '%' }"></div>
                </div>
              </div>

              <!-- Goal Details -->
              <div class="goal-details">
                <div class="detail-item">
                  <i class="fa-solid fa-calendar-days me-1"></i>
                  <span>{{ this.getGoalRemainingDays(goal) || 0 }} days left</span>
                </div>
                <div class="detail-item">
                  <i class="fa-solid fa-piggy-bank me-1"></i>
                  <span>${{ formatCurrency(this.getGoalDailyAllocation(goal) || 0) }}/day</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Header -->
      <div class="card mb-3">
        <div class="card-header">
          <div class="header-icon">
            <i class="fa-solid fa-piggy-bank"></i>
          </div>
          <div class="header-content">
            <h3 class="card-title">Savings Goals</h3>
            <p class="card-subtitle">Track your progress toward financial objectives</p>
          </div>
        </div>

        <!-- Summary Section (Requirements 2.1, 7.5) -->
        <div class="card-body">
          <div class="savings-summary nested-container">
            <div class="summary-stat mb-3">
              <div class="stat-icon active-icon">
                <i class="fa-solid fa-bullseye"></i>
              </div>
              <div class="stat-content">
                <span class="stat-label">Active Goals</span>
                <span class="stat-value">{{ activeGoals.length }}</span>
              </div>
            </div>

            <div class="summary-stat mb-3">
              <div class="stat-icon completed-icon">
                <i class="fa-solid fa-check-circle"></i>
              </div>
              <div class="stat-content">
                <span class="stat-label">Completed</span>
                <span class="stat-value">{{ completedGoals.length }}</span>
              </div>
            </div>

            <div class="summary-stat mb-3">
              <div class="stat-icon allocation-icon">
                <i class="fa-solid fa-coins"></i>
              </div>
              <div class="stat-content">
                <span class="stat-label">Daily Target</span>
                <span class="stat-value allocation-value">
                  ${{ formatCurrency(totalDailyAllocation) }}
                </span>
              </div>
            </div>

            <div class="summary-stat mb-0">
              <div class="stat-icon paused-icon">
                <i class="fa-solid fa-pause-circle"></i>
              </div>
              <div class="stat-content">
                <span class="stat-label">Paused Goals</span>
                <span class="stat-value">{{ pausedGoals.length }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Goal Creation Form -->
      <GoalForm @goal-created="handleGoalCreated" />

      <!-- Goals List Section -->
      <div class="goals-list-section">

        <!-- Paused Goals -->
        <div v-if="pausedGoals.length > 0" class="card mb-4">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-pause-circle"></i>
            </div>
            <div class="header-content">
              <h3 class="card-title">Paused Goals ({{ pausedGoals.length }})</h3>
              <p class="card-subtitle">Goals temporarily paused and not affecting your budget</p>
            </div>
          </div>
          <div class="card-body">
            <div class="goals-grid">
              <div v-for="goal in pausedGoals" :key="goal.id" class="goal-item nested-container goal-paused">
                <!-- Goal Header -->
                <div class="goal-header">
                  <div class="goal-info">
                    <h5 class="goal-name">{{ goal.name }}</h5>
                    <span class="goal-status">{{ getGoalStatusText(goal) }}</span>
                  </div>
                  <div class="goal-actions">
                    <div class="dropdown">
                      <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button"
                        :id="`goalActions${goal.id}`" data-bs-toggle="dropdown" aria-expanded="false">
                        <i class="fa-solid fa-ellipsis-vertical"></i>
                      </button>
                      <ul class="dropdown-menu" :aria-labelledby="`goalActions${goal.id}`">
                        <li>
                          <button class="dropdown-item" @click="handleResumeGoal(goal.id)">
                            <i class="fa-solid fa-play me-2"></i>Resume Goal
                          </button>
                        </li>
                        <li>
                          <button class="dropdown-item" @click="handleMarkCompleted(goal.id)">
                            <i class="fa-solid fa-check me-2"></i>Mark Complete
                          </button>
                        </li>
                        <li>
                          <hr class="dropdown-divider">
                        </li>
                        <li>
                          <button class="dropdown-item text-danger" @click="handleDeleteGoal(goal.id)">
                            <i class="fa-solid fa-trash me-2"></i>Delete Goal
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <!-- Goal Progress -->
                <div class="goal-progress">
                  <div class="progress-info">
                    <span class="progress-text">
                      ${{ formatCurrency(this.getGoalProgress(goal.id) || 0) }} of ${{ formatCurrency(goal.targetAmount)
                      }}
                    </span>
                    <span class="progress-percentage">{{ Math.round(this.getGoalProgressPercentage(goal) || 0)
                    }}%</span>
                  </div>
                  <div class="progress">
                    <div class="progress-bar" :class="getProgressBarClass(goal)" role="progressbar"
                      :style="{ width: Math.min(100, this.getGoalProgressPercentage(goal) || 0) + '%' }"></div>
                  </div>
                </div>

                <!-- Goal Details -->
                <div class="goal-details">
                  <div class="detail-item">
                    <i class="fa-solid fa-calendar-days me-1"></i>
                    <span>{{ goal.remainingDays || 0 }} days left</span>
                  </div>
                  <div class="detail-item">
                    <i class="fa-solid fa-piggy-bank me-1"></i>
                    <span>${{ formatCurrency(this.getGoalDailyAllocation(goal) || 0) }}/day</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Completed Goals -->
        <div v-if="completedGoals.length > 0" class="card">
          <div class="card-header">
            <div class="header-icon">
              <i class="fa-solid fa-trophy"></i>
            </div>
            <div class="header-content">
              <h3 class="card-title">Completed Goals ({{ completedGoals.length }})</h3>
              <p class="card-subtitle">Congratulations! You've achieved these savings objectives</p>
            </div>
          </div>
          <div class="card-body">
            <div class="goals-grid">
              <div v-for="goal in completedGoals" :key="goal.id" class="goal-item-completed nested-container">
                <!-- Canvas confetti overlay -->
                <canvas :ref="el => setCompletedGoalCanvas(goal.id, el)" class="confetti-canvas">
                </canvas>

                <!-- Goal Header -->
                <div class="completed-goal-header">
                  <div class="trophy-icon">
                    <i class="fa-solid fa-trophy"></i>
                  </div>
                  <h5 class="completed-goal-name">{{ goal.name }}</h5>
                </div>

                <!-- Goal Amount -->
                <div class="completed-goal-amount">
                  <span class="amount-value">${{ formatCurrency(goal.targetAmount) }}</span>
                </div>

                <!-- Goal Status -->
                <div class="completed-goal-status">
                  <span class="status-badge">
                    <i class="fa-solid fa-check-circle me-1"></i>
                    Goal Achieved!
                  </span>
                </div>

                <!-- Goal Details -->
                <div class="completed-goal-details">
                  <div class="detail-row">
                    <span class="detail-label">
                      <i class="fa-solid fa-calendar-check me-1"></i>
                      Duration
                    </span>
                    <span class="detail-value">{{ goal.duration }} days</span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <i class="fa-solid fa-piggy-bank me-1"></i>
                      Total Saved
                    </span>
                    <span class="detail-value">${{ formatCurrency(this.getGoalProgress(goal.id) || 0) }}</span>
                  </div>
                </div>

                <!-- Actions -->
                <div class="completed-goal-actions">
                  <button class="btn btn-outline-primary mt-2" @click="handleDeleteGoal(goal.id)">
                    <i class="fa-solid fa-trash me-1"></i>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="goals.length === 0 && !isLoading" class="empty-state">
          <div class="card">
            <div class="card-body text-center py-5">
              <div class="empty-icon mb-3">
                <i class="fa-solid fa-piggy-bank text-muted"></i>
              </div>
              <h5 class="text-muted">No Savings Goals Yet</h5>
              <p class="text-muted mb-4">
                Create your first savings goal to start working toward something meaningful.
                Set a target amount and timeframe, and we'll help you pace your progress.
              </p>
            </div>
          </div>
        </div>

        <!-- No Active Goals (but has goals) -->
        <div v-else-if="activeGoals.length === 0 && goals.length > 0 && !isLoading" class="no-active-goals">
          <div class="card">
            <div class="card-body text-center py-4">
              <div class="info-icon mb-3">
                <i class="fa-solid fa-info-circle text-info"></i>
              </div>
              <h6 class="text-info">No Active Goals</h6>
              <p class="text-muted mb-3">
                All your goals are either completed or paused. Create a new goal or resume a paused one to keep building
                toward your next milestone.
              </p>
              <button class="btn btn-outline-primary" @click="scrollToForm">
                <i class="fa-solid fa-plus me-2"></i>
                Create New Goal
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Success Toast for Goal Actions -->
    <div v-if="showSuccessToast" class="toast-container position-fixed bottom-0 end-0 p-3">
      <div class="toast show" role="alert">
        <div class="toast-header">
          <i class="fa-solid fa-check-circle text-success me-2"></i>
          <strong class="me-auto">Success</strong>
          <button type="button" class="btn-close" @click="hideSuccessToast"></button>
        </div>
        <div class="toast-body">
          {{ successMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSavingsGoals } from '../composables/useSavingsGoals.js';
import { useErrorModal } from '../composables/useErrorModal.js';
import { useAuth } from '../composables/useAuth.js';
import { useTodayCalculations } from '../composables/useTodayCalculations.js';
import { useRemainingBudget } from '../composables/useRemainingBudget.js';
import { updateGoalProgress } from '../utils/savingsGoalsService.ts';
import GoalForm from './GoalForm.vue';

export default {
  name: 'SavingsGoals',
  components: {
    GoalForm
  },
  emits: ['contribution-made', 'transaction-created', 'goals-progress-updated'],
  setup() {
    const {
      goals,
      isLoading,
      error,
      activeGoals,
      completedGoals,
      pausedGoals,
      totalDailyAllocation,
      activeGoalsProgressPercentage,
      loadGoals,
      updateGoal,
      // Calculation functions from composable
      getGoalProgress,
      getGoalStatus,
      getGoalProgressPercentage,
      getGoalActiveDaysElapsed,
      getGoalRemainingDays,
      getGoalDailyAllocation,
      getGoalAdjustedEndDate,
      getGoalCalculatedProperties
    } = useSavingsGoals();

    const { showError } = useErrorModal();
    const { user, getAccessToken } = useAuth();
    const { todayRemainingBudget } = useTodayCalculations();
    const { adjustedRemainingBudget, formatCurrency } = useRemainingBudget();

    return {
      goals,
      isLoading,
      error,
      activeGoals,
      completedGoals,
      pausedGoals,
      totalDailyAllocation,
      loadGoals,
      updateGoal,
      showError,
      user,
      getAccessToken,
      todayRemainingBudget,
      adjustedRemainingBudget,
      formatCurrency,
      activeGoalsProgressPercentage,
      // Goal calculation methods from composable
      getGoalProgress,
      getGoalStatus,
      getGoalProgressPercentage,
      getGoalActiveDaysElapsed,
      getGoalRemainingDays,
      getGoalDailyAllocation,
      getGoalAdjustedEndDate,
      getGoalCalculatedProperties,
    };
  },
  data() {
    return {
      showSuccessToast: false,
      successMessage: '',
      toastTimeout: null,
      isDistributing: false,
      isDistributingToday: false,
      distributionAmount: 0, // Will be set to todayRemainingBudget when available
      contributingGoals: new Set(), // Track which goals are currently processing contributions
      sliderReady: false, // Track when slider dimensions are available
      completedGoalCanvases: new Map(), // Map of goal IDs to canvas refs
      confettiAnimations: new Map(), // Map of goal IDs to animation IDs
      confettiParticles: new Map() // Map of goal IDs to particle arrays
    };
  },
  watch: {
    // Set default distribution amount to today's remaining budget
    todayRemainingBudget: {
      handler(newValue) {
        // Only set if distributionAmount is still 0 (initial state)
        if (this.distributionAmount === 0 && newValue > 0) {
          this.distributionAmount = Math.min(newValue, this.adjustedRemainingBudget);
        }
      },
      immediate: true
    },
    // Reinitialize confetti when completed goals change
    completedGoals: {
      handler() {
        // Stop existing confetti
        this.stopAllConfetti();
        // Reinitialize for new completed goals
        this.initAllCompletedGoalConfetti();
      },
      deep: true
    }
  },
  methods: {
    // Format currency values
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    // Emit goals progress update event
    emitGoalsProgressUpdate() {
      this.$emit('goals-progress-updated', {
        percentComplete: this.activeGoalsProgressPercentage,
        totalTarget: this.activeGoals.reduce((sum, goal) => sum + goal.targetAmount, 0),
        totalProgress: this.activeGoals.reduce((sum, goal) => sum + (this.getGoalProgress(goal.id) || 0), 0),
        activeGoalsCount: this.activeGoals.length
      });
    },

    // Get goal status text
    getGoalStatusText(goal) {
      switch (this.getGoalStatus(goal)) {
        case 'active':
          return `${this.getGoalRemainingDays(goal) || 0} days remaining`;
        case 'completed':
          return 'Goal completed!';
        case 'paused':
          return 'Goal paused';
        default:
          return '';
      }
    },

    // Get progress bar class
    getProgressBarClass(goal) {
      const progress = this.getGoalProgressPercentage(goal);
      if (progress >= 100) {
        return 'bg-success';
      }
      if (progress >= 75) {
        return 'bg-info';
      }
      if (progress >= 50) {
        return 'bg-warning';
      }
      return 'bg-primary';
    },

    // Handle goal actions
    async handlePauseGoal(goalId) {
      try {
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to pause goals.');
          return;
        }

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        const goal = this.goals.find(g => g.id === goalId);
        if (!goal) {
          this.showError('Goal Not Found', 'The selected goal could not be found.');
          return;
        }

        // Import and use the pause service
        const { pauseSavingsGoal } = await import('../utils/savingsGoalsService.ts');
        await pauseSavingsGoal(goalId, accessToken);

        // Update local state
        await this.updateGoal(goalId, {
          status: 'paused',
          pausedDate: new Date().toISOString()
        });

        this.showSuccessMessage(`Goal "${goal.name}" has been paused.`);
      } catch (error) {
        console.error('Failed to pause goal:', error);
        this.showError('Pause Failed', 'Unable to pause the goal. Please try again.');
      }
    },

    async handleResumeGoal(goalId) {
      try {
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to resume goals.');
          return;
        }

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        const goal = this.goals.find(g => g.id === goalId);
        if (!goal) {
          this.showError('Goal Not Found', 'The selected goal could not be found.');
          return;
        }

        // Import and use the resume service
        const { resumeSavingsGoal } = await import('../utils/savingsGoalsService.ts');
        await resumeSavingsGoal(goalId, accessToken);

        // Calculate days paused for local state update
        const daysPaused = goal.pausedDate ?
          Math.ceil((new Date() - new Date(goal.pausedDate)) / (1000 * 60 * 60 * 24)) : 0;

        // Update local state
        await this.updateGoal(goalId, {
          status: 'active',
          resumedDate: new Date().toISOString(),
          totalPausedDays: (goal.totalPausedDays || 0) + daysPaused,
          pausedDate: null
        });

        this.showSuccessMessage(`Goal "${goal.name}" has been resumed.`);
      } catch (error) {
        console.error('Failed to resume goal:', error);
        this.showError('Resume Failed', 'Unable to resume the goal. Please try again.');
      }
    },

    async handleMarkCompleted(goalId) {
      try {
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to mark goals as completed.');
          return;
        }

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        const goal = this.goals.find(g => g.id === goalId);
        if (!goal) {
          this.showError('Goal Not Found', 'The selected goal could not be found.');
          return;
        }

        // Import and use the mark completed service
        const { markGoalCompleted } = await import('../utils/savingsGoalsService.ts');
        await markGoalCompleted(goalId, accessToken);

        // Update local state
        await this.updateGoal(goalId, { status: 'completed' });

        this.showSuccessMessage(`🎉 Goal "${goal.name}" marked as completed!`);
      } catch (error) {
        console.error('Failed to mark goal as completed:', error);
        this.showError('Update Failed', 'Unable to mark the goal as completed. Please try again.');
      }
    },

    async handleDeleteGoal(goalId) {
      try {
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to delete goals.');
          return;
        }

        const goal = this.goals.find(g => g.id === goalId);
        if (!goal) {
          this.showError('Goal Not Found', 'The selected goal could not be found.');
          return;
        }

        // Confirm deletion
        if (!confirm(`Are you sure you want to delete the goal "${goal.name}"? This action cannot be undone.`)) {
          return;
        }

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        // Import and use the delete service
        const { deleteSavingsGoal } = await import('../utils/savingsGoalsService.ts');
        await deleteSavingsGoal(goalId, accessToken);

        // Update local state by removing the goal
        const goalIndex = this.goals.findIndex(g => g.id === goalId);
        if (goalIndex !== -1) {
          this.goals.splice(goalIndex, 1);
        }

        this.showSuccessMessage(`Goal "${goal.name}" has been deleted.`);
      } catch (error) {
        console.error('Failed to delete goal:', error);
        this.showError('Delete Failed', 'Unable to delete the goal. Please try again.');
      }
    },

    // Handle quick contribution (add daily allocation to goal progress)
    async handleQuickContribution(goalId, amount) {
      try {
        const goal = this.goals.find(g => g.id === goalId);
        if (!goal) {
          this.showError('Goal Not Found', 'The selected goal could not be found.');
          return;
        }

        if (this.getGoalStatus(goal) !== 'active') {
          this.showError('Goal Not Active', 'Only active goals can receive contributions.');
          return;
        }

        // Check if user is authenticated for API calls
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to update goal progress.');
          return;
        }

        // Add goal to contributing set to show loading state
        this.contributingGoals.add(goalId);

        const newProgress = (goal.actualProgress || 0) + amount;

        // Get access token for API call
        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        // Create a transaction to track this savings contribution
        // This ensures the money is properly deducted from today's remaining budget
        const { createTransaction } = await import('../utils/transactionService.ts');

        const savingsTransaction = {
          description: `Savings contribution to "${goal.name}"`,
          amount: amount,
          type: 'savings', // Use savings transaction type for proper toast styling
          date: new Date(), // Today's date
          category: 'Savings', // Categorize as savings
          goal: goal.id // Associate with the specific savings goal
        };

        // Create the transaction first to ensure budget tracking
        const transactionResult = await createTransaction(savingsTransaction, accessToken);
        const createdTransaction = {
          ...savingsTransaction,
          id: transactionResult.data.transaction.SK,
          date: new Date(transactionResult.data.transaction.createdAt)
        };

        // No need to manually update progress - it's calculated from transactions
        // Just refresh the goal calculations to reflect the new transaction
        await this.loadGoals();

        // Emit event to parent to refresh transaction data and budget calculations
        // This ensures the Today's Remaining component updates immediately
        this.$emit('contribution-made', {
          goalId,
          amount,
          transaction: createdTransaction
        });
        this.$emit('transaction-created', createdTransaction);
        
        // Emit overall goals progress update
        this.emitGoalsProgressUpdate();
      } catch (error) {
        console.error('Failed to add contribution:', error);

        // Show specific error message based on error type
        let errorMessage = 'Unable to add contribution to the goal. Please try again.';
        if (error.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message?.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please sign in again.';
        } else if (error.statusCode === 404) {
          errorMessage = 'Goal not found on server. It may have been deleted.';
        } else if (error.statusCode >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        this.showError('Contribution Failed', errorMessage);
      } finally {
        // Remove goal from contributing set to hide loading state
        this.contributingGoals.delete(goalId);
      }
    },

    // Handle distributing remaining budget across all active goals
    async handleDistributeRemainingBudget() {
      try {
        if (this.activeGoals.length === 0) {
          this.showError('No Active Goals', 'You need at least one active goal to distribute remaining budget.');
          return;
        }

        if (this.adjustedRemainingBudget <= 0) {
          this.showError('No Remaining Budget', 'You have no remaining budget to distribute today.');
          return;
        }

        // Check if user is authenticated
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to distribute remaining budget.');
          return;
        }

        // Confirm the action
        const totalAmount = this.adjustedRemainingBudget;
        const amountPerGoal = totalAmount / this.activeGoals.length;
        const goalNames = this.activeGoals.map(g => g.name).join(', ');
        const confirmMessage = `Distribute $${this.formatCurrency(totalAmount)} evenly across ${this.activeGoals.length} active goal${this.activeGoals.length > 1 ? 's' : ''}?\n\nGoals: ${goalNames}\nEach goal will receive: $${this.formatCurrency(amountPerGoal)}`;

        if (!confirm(confirmMessage)) {
          return;
        }

        this.isDistributing = true;

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        // Import transaction service
        const { createTransaction } = await import('../utils/transactionService.ts');

        // Create individual transactions for each goal and update progress
        const goalUpdatePromises = this.activeGoals.map(async (goal) => {
          const contributionAmount = amountPerGoal;

          // Create individual transaction for this goal
          const goalTransaction = {
            description: `Savings contribution to "${goal.name}"`,
            amount: contributionAmount,
            type: 'savings',
            date: new Date(),
            category: 'Savings',
            goal: goal.id // Associate with the specific savings goal
          };

          // Create the transaction
          const transactionResult = await createTransaction(goalTransaction, accessToken);
          const createdTransaction = {
            ...goalTransaction,
            id: transactionResult.data.transaction.SK,
            date: new Date(transactionResult.data.transaction.createdAt)
          };

          // No need to manually update progress - it's calculated from transactions
          // The transaction creation is sufficient

          return {
            goalId: goal.id,
            goalName: goal.name,
            amount: contributionAmount,
            transaction: createdTransaction
          };
        });

        // Execute all goal updates and transaction creations
        const goalUpdates = await Promise.all(goalUpdatePromises);

        // Refresh goals to recalculate progress from transactions
        await this.loadGoals();

        // Emit events for all created transactions
        goalUpdates.forEach(update => {
          this.$emit('transaction-created', update.transaction);
        });

        // Emit overall goals progress update
        this.emitGoalsProgressUpdate();

        // Show success message
        const completedGoals = goalUpdates.filter(update => {
          const goal = this.activeGoals.find(g => g.id === update.goalId);
          return goal && (goal.actualProgress || 0) + update.amount >= goal.targetAmount;
        });

        let successMsg = `🎉 Successfully contributed $${this.formatCurrency(totalAmount)} across ${goalUpdates.length} goal${goalUpdates.length > 1 ? 's' : ''}!`;

        if (completedGoals.length > 0) {
          successMsg += ` ${completedGoals.length} goal${completedGoals.length > 1 ? 's' : ''} completed!`;
        }
      } catch (error) {
        console.error('Failed to contribute remaining budget:', error);

        let errorMessage = 'Unable to contribute remaining budget. Please try again.';
        if (error.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message?.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please sign in again.';
        } else if (error.statusCode >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        this.showError('Distribution Failed', errorMessage);
      } finally {
        this.isDistributing = false;
      }
    },

    // Handle distributing today's remaining budget across all active goals
    async handleDistributeTodayRemaining() {
      try {
        if (this.activeGoals.length === 0) {
          this.showError('No Active Goals', 'You need at least one active goal to contribute today\'s remaining target.');
          return;
        }

        if (this.todayRemainingBudget <= 0) {
          this.showError('No Remaining Target Today', 'You have no remaining target from today to contribute.');
          return;
        }

        // Check if user is authenticated
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to contribute today\'s remaining target.');
          return;
        }

        // Confirm the action
        const totalAmount = this.todayRemainingBudget;
        const amountPerGoal = totalAmount / this.activeGoals.length;
        const goalNames = this.activeGoals.map(g => g.name).join(', ');
        const confirmMessage = `Contribute today's remaining target of $${this.formatCurrency(totalAmount)} evenly across ${this.activeGoals.length} active goal${this.activeGoals.length > 1 ? 's' : ''}?\n\nGoals: ${goalNames}\nEach goal will receive: $${this.formatCurrency(amountPerGoal)}`;

        if (!confirm(confirmMessage)) {
          return;
        }

        this.isDistributingToday = true;

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        // Import transaction service
        const { createTransaction } = await import('../utils/transactionService.ts');

        // Create individual transactions for each goal
        const goalUpdatePromises = this.activeGoals.map(async (goal) => {
          const contributionAmount = amountPerGoal;

          // Create individual transaction for this goal
          const goalTransaction = {
            description: `Today's savings contribution to "${goal.name}"`,
            amount: contributionAmount,
            type: 'savings',
            date: new Date(),
            category: 'Savings',
            goal: goal.id // Associate with the specific savings goal
          };

          // Create the transaction
          const transactionResult = await createTransaction(goalTransaction, accessToken);
          const createdTransaction = {
            ...goalTransaction,
            id: transactionResult.data.transaction.SK,
            date: new Date(transactionResult.data.transaction.createdAt)
          };

          return {
            goalId: goal.id,
            goalName: goal.name,
            amount: contributionAmount,
            transaction: createdTransaction
          };
        });

        // Execute all goal updates and transaction creations
        const goalUpdates = await Promise.all(goalUpdatePromises);

        // Refresh goals to recalculate progress from transactions
        await this.loadGoals();

        // Emit events for all created transactions
        goalUpdates.forEach(update => {
          this.$emit('transaction-created', update.transaction);
        });

        // Emit overall goals progress update
        this.emitGoalsProgressUpdate();

        // Show success message
        const completedGoals = goalUpdates.filter(update => {
          const goal = this.activeGoals.find(g => g.id === update.goalId);
          return goal && (goal.actualProgress || 0) + update.amount >= goal.targetAmount;
        });

        let successMsg = `🎉 Successfully contributed today's remaining target of $${this.formatCurrency(totalAmount)} across ${goalUpdates.length} goal${goalUpdates.length > 1 ? 's' : ''}!`;

        if (completedGoals.length > 0) {
          successMsg += ` ${completedGoals.length} goal${completedGoals.length > 1 ? 's' : ''} completed!`;
        }

        this.showSuccessMessage(successMsg);
      } catch (error) {
        console.error('Failed to contribute today\'s remaining target:', error);

        let errorMessage = 'Unable to contribute today\'s remaining target. Please try again.';
        if (error.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message?.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please sign in again.';
        } else if (error.statusCode >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        this.showError('Distribution Failed', errorMessage);
      } finally {
        this.isDistributingToday = false;
      }
    },

    // Handle slider input with snap functionality
    handleSliderInput(event) {
      const value = parseFloat(event.target.value);
      const snapThreshold = this.adjustedRemainingBudget * 0.02; // 2% of max value for snap zone

      // Snap to today's remaining budget if close enough
      if (this.todayRemainingBudget > 0 &&
        Math.abs(value - this.todayRemainingBudget) <= snapThreshold) {
        this.distributionAmount = this.todayRemainingBudget;
      } else {
        this.distributionAmount = value;
      }
    },

    // Calculate marker position as percentage, accounting for thumb width
    getMarkerPosition(value) {
      if (this.adjustedRemainingBudget <= 0) return 0;

      // Calculate the percentage position
      const percentage = (value / this.adjustedRemainingBudget) * 100;

      // Account for thumb width (20px) relative to slider width
      // The thumb centers itself, so we need to adjust for half the thumb width
      const thumbWidth = 20; // matches CSS thumb width
      const sliderElement = document.getElementById('distributionSlider');

      if (this.sliderReady && sliderElement && sliderElement.offsetWidth > 0) {
        const sliderWidth = sliderElement.offsetWidth;
        const thumbOffset = (thumbWidth / 2) / sliderWidth * 100;

        // Adjust position: at 0% add offset, at 100% subtract offset
        const adjustedPercentage = percentage + thumbOffset - (2 * thumbOffset * percentage / 100);
        return Math.max(0, Math.min(100, adjustedPercentage));
      }

      // Fallback: use a reasonable default thumb offset for initial render
      // Assume a typical slider width of 300px for initial calculation
      const assumedSliderWidth = 300;
      const thumbOffset = (thumbWidth / 2) / assumedSliderWidth * 100;
      const adjustedPercentage = percentage + thumbOffset - (2 * thumbOffset * percentage / 100);
      return Math.max(0, Math.min(100, adjustedPercentage));
    },

    // Snap to today's remaining budget
    snapToTodayBudget() {
      if (this.todayRemainingBudget > 0) {
        this.distributionAmount = Math.min(this.todayRemainingBudget, this.adjustedRemainingBudget);
      }
    },


    async handleDistributeCustomAmount() {
      try {
        if (this.activeGoals.length === 0) {
          this.showError('No Active Goals', 'You need at least one active goal to distribute funds.');
          return;
        }

        if (this.distributionAmount <= 0) {
          this.showError('Invalid Amount', 'Please select an amount greater than $0 to distribute.');
          return;
        }

        if (this.distributionAmount > this.adjustedRemainingBudget) {
          this.showError('Amount Too High', 'The selected amount exceeds your available target.');
          return;
        }

        // Check if user is authenticated
        if (!this.user) {
          this.showError('Authentication Required', 'Please sign in to contribute.');
          return;
        }

        // Confirm the action
        const totalAmount = this.distributionAmount;
        const amountPerGoal = totalAmount / this.activeGoals.length;
        const goalNames = this.activeGoals.map(g => g.name).join(', ');
        const confirmMessage = `Contribute $${this.formatCurrency(totalAmount)} evenly across ${this.activeGoals.length} active goal${this.activeGoals.length > 1 ? 's' : ''}?\n\nGoals: ${goalNames}\nEach goal will receive: $${this.formatCurrency(amountPerGoal)}`;

        if (!confirm(confirmMessage)) {
          return;
        }

        this.isDistributing = true;

        const accessToken = await this.getAccessToken();
        if (!accessToken) {
          this.showError('Authentication Error', 'Unable to authenticate. Please sign in again.');
          return;
        }

        // Import transaction service
        const { createTransaction } = await import('../utils/transactionService.ts');

        // Create individual transactions for each goal
        const goalUpdatePromises = this.activeGoals.map(async (goal) => {
          const contributionAmount = amountPerGoal;

          // Create individual transaction for this goal
          const goalTransaction = {
            description: `Savings contribution to "${goal.name}"`,
            amount: contributionAmount,
            type: 'savings',
            date: new Date(),
            category: 'Savings',
            goal: goal.id // Associate with the specific savings goal
          };

          // Create the transaction
          const transactionResult = await createTransaction(goalTransaction, accessToken);
          const createdTransaction = {
            ...goalTransaction,
            id: transactionResult.data.transaction.SK,
            date: new Date(transactionResult.data.transaction.createdAt)
          };

          return {
            goalId: goal.id,
            goalName: goal.name,
            amount: contributionAmount,
            transaction: createdTransaction
          };
        });

        // Execute all goal updates and transaction creations
        const goalUpdates = await Promise.all(goalUpdatePromises);

        // Refresh goals to recalculate progress from transactions
        await this.loadGoals();

        // Reset slider to 0 after successful distribution
        this.distributionAmount = 0;

        // Emit events for all created transactions
        goalUpdates.forEach(update => {
          this.$emit('transaction-created', update.transaction);
        });

        // Show success message
        const completedGoals = goalUpdates.filter(update => {
          const goal = this.activeGoals.find(g => g.id === update.goalId);
          return goal && (goal.actualProgress || 0) + update.amount >= goal.targetAmount;
        });

        let successMsg = `🎉 Successfully contributed $${this.formatCurrency(totalAmount)} across ${goalUpdates.length} goal${goalUpdates.length > 1 ? 's' : ''}!`;

        if (completedGoals.length > 0) {
          successMsg += ` ${completedGoals.length} goal${completedGoals.length > 1 ? 's' : ''} completed!`;
        }

        this.showSuccessMessage(successMsg);
      } catch (error) {
        console.error('Failed to contribute custom amount:', error);

        let errorMessage = 'Unable to contribute the selected amount. Please try again.';
        if (error.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message?.includes('Authentication')) {
          errorMessage = 'Authentication failed. Please sign in again.';
        } else if (error.statusCode >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        this.showError('Distribution Failed', errorMessage);
      } finally {
        this.isDistributing = false;
      }
    },
    handleGoalCreated(goalData) {

      // Scroll to the newly created goal (it will be in active goals)
      this.$nextTick(() => {
        const activeGoalsSection = document.querySelector('.goals-section');
        if (activeGoalsSection) {
          activeGoalsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },

    // Handle goal updates
    handleGoalUpdated(goalId) {
      const updatedGoal = this.goals.find(g => g.id === goalId);
      if (updatedGoal) {
        this.showSuccessMessage(`Goal "${updatedGoal.name}" updated successfully!`);
      }
    },

    // Handle goal deletion
    handleGoalDeleted(goalId) {
      this.showSuccessMessage('Goal deleted successfully!');
    },

    // Handle retry loading
    async handleRetryLoad() {
      try {
        await this.loadGoals();
      } catch (error) {
        console.error('Failed to retry loading goals:', error);
        this.showError(
          'Failed to Load Goals',
          'Unable to load your savings goals. Please check your connection and try again.'
        );
      }
    },

    // Scroll to form for easy goal creation
    scrollToForm() {
      const formElement = document.querySelector('.goal-form, form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });

        // Focus on the first input field
        this.$nextTick(() => {
          const firstInput = formElement.querySelector('input');
          if (firstInput) {
            firstInput.focus();
          }
        });
      }
    },

    // Show success message with auto-hide
    showSuccessMessage(message) {
    },

    // Hide success toast
    hideSuccessToast() {
      this.showSuccessToast = false;
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
        this.toastTimeout = null;
      }
    },

    // Set canvas ref for completed goal
    setCompletedGoalCanvas(goalId, el) {
      if (el) {
        this.completedGoalCanvases.set(goalId, el);
      }
    },

    // Initialize confetti for a completed goal
    initGoalConfetti(goalId, canvas) {
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const rect = canvas.parentElement.getBoundingClientRect();
      const devicePixelRatio = window.devicePixelRatio || 1;

      // Set display size (CSS pixels)
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      // Set actual size in memory (physical pixels)
      canvas.width = Math.round(rect.width * devicePixelRatio);
      canvas.height = Math.round(rect.height * devicePixelRatio);

      // Reset transform before scaling
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(devicePixelRatio, devicePixelRatio);

      // Import confetti utilities
      import('../utils/confetti.ts').then(({ createConfettiParticles }) => {
        // Create confetti particles using CSS dimensions
        const particles = createConfettiParticles(
          rect.width,
          rect.height,
          { particleCount: 25 }
        );

        this.confettiParticles.set(goalId, particles);
        this.animateGoalConfetti(goalId);
      });
    },

    // Animate confetti for a specific goal
    animateGoalConfetti(goalId) {
      const canvas = this.completedGoalCanvases.get(goalId);
      const particles = this.confettiParticles.get(goalId);

      if (!canvas || !particles) return;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw all particles
      particles.forEach(particle => {
        particle.update();
        particle.draw(ctx);
      });

      // Continue animation indefinitely
      const animationId = requestAnimationFrame(() => this.animateGoalConfetti(goalId));
      this.confettiAnimations.set(goalId, animationId);
    },

    // Stop confetti for a specific goal
    stopGoalConfetti(goalId) {
      const animationId = this.confettiAnimations.get(goalId);
      if (animationId) {
        cancelAnimationFrame(animationId);
        this.confettiAnimations.delete(goalId);
      }

      const canvas = this.completedGoalCanvases.get(goalId);
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      this.confettiParticles.delete(goalId);
    },

    // Initialize all completed goal confetti
    initAllCompletedGoalConfetti() {
      this.$nextTick(() => {
        this.completedGoals.forEach(goal => {
          const canvas = this.completedGoalCanvases.get(goal.id);
          if (canvas) {
            this.initGoalConfetti(goal.id, canvas);
          }
        });
      });
    },

    // Stop all confetti animations
    stopAllConfetti() {
      this.confettiAnimations.forEach((animationId, goalId) => {
        cancelAnimationFrame(animationId);
      });
      this.confettiAnimations.clear();
      this.confettiParticles.clear();
    }
  },
  mounted() {
    // Force marker position recalculation after DOM is ready
    this.$nextTick(() => {
      // Mark slider as ready for accurate positioning
      this.sliderReady = true;
      // Trigger a reactive update to recalculate marker positions
      this.$forceUpdate();
    });

    // Initialize confetti for completed goals
    this.initAllCompletedGoalConfetti();
  },
  beforeUnmount() {
    // Clean up timeout
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    // Stop all confetti animations
    this.stopAllConfetti();
  }
};
</script>

<style scoped>
.dist-btn {
  width: auto !important;
}

/* Container styling */
.savings-goals-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header icon styling */
.header-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--light-text);
  font-size: 1.25rem;
  flex-shrink: 0;
  border: 1px solid var(--light-text);
}

/* Summary statistics */
.savings-summary {
  /* Additional styling beyond nested-container */
}

.summary-stat {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  min-width: 0;
  /* Prevent flex items from shrinking too much */
}

.summary-stat:hover {
  background: rgba(0, 0, 0, 0.02);
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.active-icon {
  background: color-mix(in srgb, var(--btn-primary-color) 5%, transparent);
  color: var(--btn-primary-color);
}

.completed-icon {
  background: color-mix(in srgb, var(--btn-primary-color) 5%, transparent);
  color: var(--btn-primary-color);
}

.allocation-icon {
  background: color-mix(in srgb, var(--btn-primary-color) 5%, transparent);
  color: var(--btn-primary-color);
}

.paused-icon {
  background: color-mix(in srgb, var(--btn-primary-color) 5%, transparent);
  color: var(--btn-primary-color);
}

.stat-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--light-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--light-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.allocation-value {
  color: var(--light-success);
}

/* Goals list sections */
.goals-list-section {
  margin-top: 1rem;
}

/* Distribute budget section */
.distribute-budget-section {
  /* Additional styling beyond nested-container */
  border: 2px dashed var(--light-border);
}

.distribute-header {
  width: 100%;
  margin-bottom: 1rem;
}

.distribute-header h6 {
  color: var(--light-text);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.distribute-info {
  display: flex;
  justify-content: center;
  align-items: center;
}

.distribute-action {
  flex-shrink: 0;
}

.distribute-preview {
  text-align: center;
  padding-top: 0.5rem;
  border-top: 1px solid var(--light-border);
}

.distribute-preview i {
  color: var(--light-text-muted);
}

/* Button styling for distribute action */
.btn-success {
  background-color: var(--btn-primary-color);
  border-color: var(--btn-primary-color);
  color: white;
  font-weight: 600;
  min-height: 44px;
  white-space: nowrap;
  transition: all 0.2s ease;
  /* Smooth transitions for all properties */
}

.btn-success:hover:not(:disabled) {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(54, 162, 235, 0.3);
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-success:active {
  transform: translateY(0);
}

/* Goals grid */
.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

/* Goal item styling */
.goal-item {
  /* Additional styling beyond nested-container */
}

.goal-paused {
  opacity: 0.7;
  border-left: 4px solid var(--light-text-muted);
}

.goal-completed {
  border-left: 4px solid var(--light-success);
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05), rgba(40, 167, 69, 0.02));
}

/* Completed goal item styling (savings-guidance style) */
.goal-item-completed {
  text-align: center;
  padding: 1.5rem 1rem;
  position: relative;
  overflow: hidden;
}

/* Canvas confetti for completed goals */
.confetti-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

/* Completed goal header */
.completed-goal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
}

.trophy-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
}

.completed-goal-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--light-text);
  margin: 0;
}

/* Completed goal amount */
.completed-goal-amount {
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
}

.completed-goal-amount .amount-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--light-text);
}

/* Completed goal status */
.completed-goal-status {
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  background: var(--light-success);
  color: white;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Completed goal details */
.completed-goal-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--light-border);
  position: relative;
  z-index: 2;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-size: 0.85rem;
  color: var(--light-text-muted);
  display: flex;
  align-items: center;
}

.detail-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--light-text);
}

/* Completed goal actions */
.completed-goal-actions {
  padding-top: 0.75rem;
  border-top: 1px solid var(--light-border);
  position: relative;
  z-index: 2;
}

/* Goal header */
.goal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.goal-info {
  flex: 1;
  min-width: 0;
}

.goal-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--light-text);
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.goal-status {
  font-size: 0.85rem;
  color: var(--light-text-muted);
}

/* Goal actions */
.goal-actions {
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Quick contribution button */
.btn-success {
  background-color: var(--light-border);
  border-color: var(--light-border);
  color: var(--light-text);
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.375rem 0.75rem;
  white-space: nowrap;
  min-width: 80px;
  /* Prevent button from shrinking during loading */
  transition: all 0.2s ease;
  /* Smooth transitions for all properties */
}

.btn-success:hover:not(:disabled) {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-success:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-success:active {
  transform: translateY(0);
}

/* Custom extra small spinner for buttons */
.spinner-border-xs {
  width: 20px;
  height: 20px;
  border-width: 3px;
  vertical-align: middle;
}

.goal-actions {
  margin-left: 0.5rem;
}

/* Goal progress */
.goal-progress {
  margin-bottom: 1rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.progress-text {
  font-size: 0.9rem;
  color: var(--light-text);
  font-weight: 500;
}

.progress-percentage {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--light-text);
}

.progress {
  height: 8px;
  border-radius: 4px;
  background-color: var(--light-border);
}

.progress-bar {
  border-radius: 4px;
  transition: width 0.3s ease;
}

/* Goal details */
.goal-details {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: var(--light-text-muted);
}

.detail-item i {
  color: var(--light-text-muted);
  margin-right: 0.25rem;
}

/* Dropdown styling */
.dropdown-toggle {
  border: none;
  background: transparent;
  color: var(--light-text-muted);
  padding: 0.25rem 0.5rem;
}

.dropdown-toggle:hover {
  color: var(--light-text);
  background: rgba(0, 0, 0, 0.05);
}

/* Loading state */
.loading-container {
  margin: 2rem 0;
}

/* Error state */
.error-container {
  margin: 2rem 0;
}

.error-icon {
  font-size: 3rem;
}

/* Empty states */
.empty-state,
.no-active-goals {
  margin: 0;
}

.empty-icon,
.info-icon {
  font-size: 3rem;
}

/* Success toast */
.toast-container {
  z-index: 1060;
}

.toast {
  background: var(--light-card);
  border: 1px solid var(--light-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.toast-header {
  background: var(--nested-container-bg);
  border-bottom: 1px solid var(--light-border);
  color: var(--light-text);
}

.toast-body {
  color: var(--light-text);
}

.btn-close {
  background: none;
  border: none;
  color: var(--light-text-muted);
  opacity: 0.7;
}

.btn-close:hover {
  opacity: 1;
}

.progress-bar {
  background-color: var(--btn-primary-color) !important;
}

/* Button styling */
.btn-primary {
  background-color: var(--btn-primary-color);
  border-color: var(--btn-primary-color);
  min-height: 44px;
  width: 140px;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
}

/* Responsive design */
@media (max-width: 768px) {
  .savings-summary {
    padding: 1rem;
  }

  .summary-stat {
    padding: 0.75rem 0.5rem;
    gap: 0.5rem;
  }

  .stat-icon {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }

  .stat-label {
    font-size: 0.75rem;
  }

  .stat-value {
    font-size: 1.1rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .section-subtitle {
    font-size: 0.85rem;
  }

  .empty-icon,
  .info-icon,
  .error-icon {
    font-size: 2.5rem;
  }

  /* Goals grid responsive */
  .goals-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .goal-item {
    padding: 0.75rem;
  }

  .goal-details {
    flex-direction: column;
    gap: 0.5rem;
  }

  /* Distribute budget section responsive */
  .distribute-budget-section {
    padding: 1rem;
  }

  .distribute-info {
    justify-content: center;
  }

  .distribute-action {
    align-self: stretch;
  }

  .btn-success {
    width: 100%;
    justify-content: center;
  }

  /* Completed goals responsive */
  .goal-item-completed {
    padding: 1rem 0.75rem;
  }

  .trophy-icon {
    width: 50px;
    height: 50px;
    font-size: 1.75rem;
  }

  .completed-goal-name {
    font-size: 1.1rem;
  }

  .completed-goal-amount .amount-value {
    font-size: 2rem;
  }
}

/* Dark mode support */
:global([data-theme]) .savings-summary {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .summary-stat:hover {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .distribute-budget-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .distribute-budget-section:hover {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .toast {
  background: var(--light-card);
  color: var(--light-text);
}

:global([data-theme]) .toast-header {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .goal-item-completed {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.15), rgba(40, 167, 69, 0.08));
}

:global([data-theme]) .trophy-icon {
  background: rgba(40, 167, 69, 0.2);
}

/* Card grid responsive behavior */
@media (min-width: 992px) {
  .col-lg-6 {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (min-width: 1200px) {
  .col-xl-4 {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
  }
}

/* Smooth transitions */
.goals-section {
  transition: all 0.3s ease;
}

.summary-stat,
.toast {
  transition: all 0.2s ease;
}

/* Focus states for accessibility */
.btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(54, 162, 235, 0.25);
}

/* Print styles */
@media print {

  .toast-container,
  .btn {
    display: none !important;
  }

  .savings-goals-container {
    max-width: none;
    padding: 0;
  }
}

/* Custom range slider styling with primary color and markers */
.slider-wrapper {
  position: relative;
  margin: 0.5rem 0;
}

.form-range {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  cursor: pointer;
  width: 100%;
  position: relative;
  z-index: 2;
}

/* Range track */
.form-range::-webkit-slider-track {
  background: var(--light-border);
  height: 10px;
  border-radius: 8px;
  border: none;
}

.form-range::-moz-range-track {
  background: var(--light-border);
  height: 10px;
  border-radius: 8px;
  border: none;
}

/* Range thumb */
.form-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  background: var(--btn-primary-color);
  height: 20px;
  width: 20px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  position: relative;
  z-index: 3;
}

.form-range::-moz-range-thumb {
  background: var(--btn-primary-color);
  height: 20px;
  width: 20px;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  position: relative;
  z-index: 3;
}

/* Hover and focus states */
.form-range:hover::-webkit-slider-thumb {
  background: var(--btn-primary-color);
  transform: scale(1.1);
}

.form-range:hover::-moz-range-thumb {
  background: var(--btn-primary-color);
  transform: scale(1.1);
}

.form-range:focus::-webkit-slider-thumb {
  background: var(--btn-primary-color);
  box-shadow: 0 0 0 3px rgba(54, 162, 235, 0.25);
}

.form-range:focus::-moz-range-thumb {
  background: var(--btn-primary-color);
  box-shadow: 0 0 0 3px rgba(54, 162, 235, 0.25);
}

/* Disabled state */
.form-range:disabled::-webkit-slider-thumb {
  background: var(--light-text-muted);
  cursor: not-allowed;
  transform: none;
}

.form-range:disabled::-moz-range-thumb {
  background: var(--light-text-muted);
  cursor: not-allowed;
  transform: none;
}

.form-range:disabled::-webkit-slider-track {
  background: rgba(var(--light-border), 0.5);
}

.form-range:disabled::-moz-range-track {
  background: rgba(var(--light-border), 0.5);
}

/* Slider markers */
.slider-marker {
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-53%);
  z-index: 1;
  cursor: pointer;
  transition: all 0.2s ease;
}

.slider-marker:hover {
  transform: translateX(-50%) translateY(-50%) scale(1.05);
}

.marker-line {
  width: 3px;
  height: 20px;
  border-radius: 2px;
  margin: 0 auto 0.25rem;
  position: relative;
}

/* Distribution legend */
.distribution-legend {
  display: flex;
  justify-content: center;
  border-bottom: 1px solid var(--light-border);
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-marker {
  width: 3px;
  height: 12px;
  background: var(--btn-primary-color);
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(54, 162, 235, 0.2);
}

.legend-text {
  font-size: 0.85rem;
  color: var(--light-text);
  font-weight: 500;
}

/* Today's budget marker styling */
.today-marker .marker-line {
  background: var(--btn-primary-color);
  box-shadow: 0 0 0 2px rgba(54, 162, 235, 0.2);
}

.today-marker:hover .marker-line {
  background: var(--btn-primary-color);
  box-shadow: 0 0 0 3px rgba(54, 162, 235, 0.3);
}

/* Dark mode support for range slider */
:global([data-theme]) .form-range::-webkit-slider-thumb {
  border-color: var(--light-card);
}

:global([data-theme]) .form-range::-moz-range-thumb {
  border-color: var(--light-card);
}

/* Responsive adjustments for markers */
@media (max-width: 768px) {
  .legend-text {
    font-size: 0.8rem;
  }
}
</style>